import React, { useState, useRef, useEffect, useCallback } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import { CONTENT, WEDDING } from "@/content";
import { PassportCover } from "@/components/PassportCover";
import { Invitation } from "@/components/Invitation";
import { Countdown } from "@/components/Countdown";
import { BoardingPass } from "@/components/BoardingPass";
import { Program } from "@/components/Program";
import { Rsvp } from "@/components/Rsvp";
import { Guestbook } from "@/components/Guestbook";
import { Gift } from "@/components/Gift";
import { TopBar, Marquee, Footer, ScrollCue } from "@/components/Controls";
import AdminPanel from "@/components/AdminPanel";
import { playOpenSfx } from "@/sfx";

const MUSIC_PRIMARY = "/audio/wedding.mp3";
const MUSIC_FALLBACK =
  "https://www.scottbuckley.com.au/library/wp-content/uploads/2021/05/sb_thelongwayhome.mp3";

function InvitationExperience() {
  const initialOpen =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("open");
  const [lang, setLang] = useState("tr");
  const [opened, setOpened] = useState(initialOpen);
  const [coverGone, setCoverGone] = useState(initialOpen);
  const [musicOn, setMusicOn] = useState(false);
  const audioRef = useRef(null);
  const lenisRef = useRef(null);
  const triedFallback = useRef(false);
  const t = CONTENT[lang];

  const handleAudioError = () => {
    const a = audioRef.current;
    if (!a || triedFallback.current) return;
    triedFallback.current = true;
    a.src = MUSIC_FALLBACK;
    a.load();
  };

  const playMusic = () => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.35;
    a.play()
      .then(() => setMusicOn(true))
      .catch(() => {
        if (!triedFallback.current) {
          triedFallback.current = true;
          a.src = MUSIC_FALLBACK;
          a.load();
          a.play().then(() => setMusicOn(true)).catch(() => setMusicOn(false));
        } else {
          setMusicOn(false);
        }
      });
  };

  // Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  // lock scroll until opened
  useEffect(() => {
    if (opened) {
      lenisRef.current?.start();
      document.body.style.overflow = "";
    } else {
      lenisRef.current?.stop();
      document.body.style.overflow = "hidden";
    }
  }, [opened]);

  // dev/deep-link: jump to a section on load (?scroll=boarding)
  useEffect(() => {
    if (!coverGone) return;
    const target = new URLSearchParams(window.location.search).get("scroll");
    if (!target) return;
    const id = setTimeout(() => {
      const el = document.getElementById(target);
      if (el && lenisRef.current) lenisRef.current.scrollTo(el, { immediate: true });
    }, 500);
    return () => clearTimeout(id);
  }, [coverGone]);

  const toggleMusic = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      playMusic();
    } else {
      a.pause();
      setMusicOn(false);
    }
  }, []);

  const handleOpen = () => {
    setOpened(true);
    playOpenSfx();
    playMusic();
    setTimeout(() => {
      lenisRef.current?.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    }, 50);
    setTimeout(() => setCoverGone(true), 1400);
  };

  return (
    <div className="App relative">
      <audio ref={audioRef} src={MUSIC_PRIMARY} onError={handleAudioError} loop preload="none" />
      <Toaster position="top-center" richColors />

      <TopBar
        lang={lang}
        setLang={setLang}
        musicOn={musicOn}
        toggleMusic={toggleMusic}
        visible={opened}
      />

      {!coverGone && (
        <PassportCover t={t.cover} opened={opened} onOpen={handleOpen} onFinished={() => setCoverGone(true)} />
      )}

      <main className={`transition-opacity duration-700 ${opened ? "opacity-100" : "opacity-0"}`}>
        <Invitation t={t.journey} />
        <Marquee />
        <Countdown t={t.countdown} />
        <BoardingPass t={t.boarding} lang={lang} />
        <Program t={t.program} />
        <Rsvp t={t.rsvp} footer={t.footer} />
        <Guestbook t={t.guestbook} />
        <Gift t={t.gift} />
        <Footer t={t.footer} whatsapp={WEDDING.whatsapp} />
      </main>

      <ScrollCue lenis={lenisRef} active={coverGone} label={lang === "tr" ? "Aşağı Kaydır" : "Scroll Down"} />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<InvitationExperience />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    </BrowserRouter>
  );
}
